import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ShieldCheck,
  CheckCircle2,
  Building2,
  DoorOpen,
  Laptop,
  ArrowLeft,
  Copy,
  Check,
  Car,
  Hourglass,
  AlertCircle,
  FileQuestion,
  UserCheck,
  UserPlus,
  Search,
  X,
  PlusCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  StatusBadge,
  Button,
  EmptyState,
  LoadingSkeleton,
} from "../../../components/ui";
import styles from "./PublicVisitorStatus.module.css";

export default function PublicVisitorStatus() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isKioskMode =
    searchParams.get("mode") === "kiosk" ||
    searchParams.get("kiosk") === "true";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Update PTM (Person to Meet) State
  const [showPtmModal, setShowPtmModal] = useState(false);
  const [ptmSearchQuery, setPtmSearchQuery] = useState("");
  const [ptmHosts, setPtmHosts] = useState([]);
  const [searchingPtmHosts, setSearchingPtmHosts] = useState(false);
  const [selectedPtmHost, setSelectedPtmHost] = useState(null);
  const [updatingPtm, setUpdatingPtm] = useState(false);

  // Poll status every 3 seconds
  useEffect(() => {
    if (!code) return;

    let isMounted = true;
    const fetchStatus = async () => {
      try {
        const res = await fetch(`/api/public/visitor/status/${code}`);
        const json = await res.json();
        if (isMounted && json.success) {
          setData(json);
          const disallowed = ["CHECKED_OUT", "CANCELLED", "REVOKED", "EXPIRED", "REJECTED"];
          const isDone = disallowed.includes(json.status) || json.is_checked_out || json.is_rejected;
          try {
            if (!isDone) {
              localStorage.setItem(
                "asmita_active_pass",
                JSON.stringify({
                  pass_code: json.pass_code || code,
                  request_number: json.request_number || code,
                  status_token: code,
                  visitor_name: json.visitor_name,
                  phone: json.visitor_phone,
                  society_id: json.society_id || 114,
                  status: json.status,
                  timestamp: Date.now(),
                })
              );
            } else {
              localStorage.removeItem("asmita_active_pass");
            }
          } catch (err) {
            // Ignore localStorage errors
          }
        }
      } catch (err) {
        console.error("Status polling error:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 3000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [code]);

  // Search candidate hosts when PTM modal opens or query changes
  useEffect(() => {
    if (!showPtmModal) return;
    const timer = setTimeout(async () => {
      setSearchingPtmHosts(true);
      try {
        const q = ptmSearchQuery.trim();
        const res = await fetch(`/api/public/visitor/hosts?query=${encodeURIComponent(q || "a")}`);
        const json = await res.json();
        if (json.success) {
          setPtmHosts(json.hosts || json.data || []);
        }
      } catch (err) {
        console.error("Error searching hosts:", err);
      } finally {
        setSearchingPtmHosts(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [showPtmModal, ptmSearchQuery]);

  const handleUpdatePtm = async () => {
    if (!selectedPtmHost) {
      toast.warn("Please select a person to meet");
      return;
    }
    setUpdatingPtm(true);
    try {
      const res = await fetch(`/api/public/visitor/status/${code}/update-ptm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          assigned_user_id: selectedPtmHost.user_id,
          department_id: selectedPtmHost.department_id,
          cabin_code: selectedPtmHost.cabin_code,
          desk_code: selectedPtmHost.desk_code,
        }),
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setData((prev) => ({
          ...prev,
          host_name: selectedPtmHost.full_name,
          department_name: selectedPtmHost.department_name || prev?.department_name,
          cabin_code: selectedPtmHost.cabin_code || prev?.cabin_code,
          desk_code: selectedPtmHost.desk_code || prev?.desk_code,
        }));
        setShowPtmModal(false);
        setSelectedPtmHost(null);
        toast.success(
          json.is_checked_in
            ? "Person to meet changed and notified."
            : `Updated to meet ${selectedPtmHost.full_name}.`
        );
      } else {
        toast.error(json.error || json.message || "Could not update person to meet");
      }
    } catch (err) {
      toast.error("Network error. Please try again.");
    } finally {
      setUpdatingPtm(false);
    }
  };

  const handleStartNewCheckin = () => {
    try {
      sessionStorage.removeItem("visitor_checkin_draft");
      sessionStorage.removeItem("visitor_checkin_phone");
    } catch (e) {
      console.warn("Could not clear session storage", e);
    }
    navigate("/visitor/checkin");
  };

  const handleCopyCode = () => {
    if (!data?.pass_code) return;
    navigator.clipboard.writeText(data.pass_code);
    setCopied(true);
    toast.success("Pass code copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading && !data) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.passCard}>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", alignItems: "center" }}>
            <LoadingSkeleton width="80px" height="80px" borderRadius="50%" />
            <LoadingSkeleton width="180px" height="24px" />
            <LoadingSkeleton width="120px" height="18px" />
            <LoadingSkeleton width="100%" height="80px" style={{ marginTop: "16px" }} />
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.passCard}>
          <EmptyState
            icon={<FileQuestion size={40} style={{ color: "var(--color-danger)" }} />}
            title="Visitor Pass Not Found"
            description="We could not locate this visitor pass code. Please scan the gate terminal QR code again or consult the security desk."
            action={
              <Button
                variant="primary"
                onClick={() => navigate("/visitor/checkin")}
                icon={<ArrowLeft size={16} />}
              >
                Return to Check-in
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  const isApproved = data.is_approved;
  const isRejected = data.is_rejected;
  const isCheckedIn = data.is_checked_in;
  const isCheckedOut = data.is_checked_out;
  const isPending = !isApproved && !isRejected && !isCheckedIn && !isCheckedOut;

  // Resolved Canonical Status for StatusBadge
  const canonicalStatus = isCheckedOut
    ? "CHECKED_OUT"
    : isCheckedIn
    ? "CHECKED_IN"
    : isApproved
    ? "APPROVED"
    : isRejected
    ? "REJECTED"
    : "AWAITING_HOST";

  const propertyDisplayName = data.society_name || "AsmitA Operations";

  return (
    <div className={styles.pageContainer}>
      <div className={`${styles.passCard} ${isPending ? styles.pulsingPulse : ""}`}>
        {/* Brand Header */}
        <div className={styles.brandHeader}>
          <ShieldCheck size={16} />
          <span>{propertyDisplayName} • Gate Pass</span>
        </div>

        {/* Visitor Avatar */}
        <div className={styles.avatarWrap}>
          {data.photo_url ? (
            <img
              src={data.photo_url}
              alt={data.visitor_name}
              className={styles.avatarImg}
            />
          ) : (
            <div className={styles.avatarFallback}>Live Photo</div>
          )}
        </div>

        {/* Visitor Name & Vehicle */}
        <h1 className={styles.visitorName}>{data.visitor_name}</h1>
        {data.vehicle_number && (
          <div className={styles.vehicleText}>
            <Car size={15} />
            <span>{data.vehicle_number}</span>
          </div>
        )}

        {/* Canonical Status Badge */}
        <div className={styles.statusWrapper}>
          <StatusBadge status={canonicalStatus} showDot />
        </div>

        {/* One-Time Pass Code Display (Shown if Approved, Checked In, or Pending) */}
        {!isRejected && data.pass_code && (
          <div className={styles.passCodeBox}>
            <div className={styles.passCodeLabel}>One-Time Gate Pass Code</div>
            <div className={styles.passCodeVal}>{data.pass_code}</div>
            <div style={{ marginTop: "10px" }}>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleCopyCode}
                icon={copied ? <Check size={14} /> : <Copy size={14} />}
              >
                {copied ? "Copied" : "Copy Code"}
              </Button>
            </div>
          </div>
        )}

        {/* Destination Information Grid */}
        <div className={styles.destGrid}>
          <div className={styles.destItem}>
            <div className={styles.destLabel}>Floor & Destination</div>
            <div className={styles.destValue}>
              Floor {data.floor_number || "1"} • {data.department_name || "General"}
            </div>
          </div>

          <div className={styles.destItem}>
            <div className={styles.destLabel}>Host / Meeting With (PTM)</div>
            <div className={styles.destValue}>
              {data.host_name || "Department Team"}
            </div>
          </div>

          <div className={styles.destItem}>
            <div className={styles.destLabel}>Destination Cabin / Unit</div>
            <div className={`${styles.badgePill} ${styles.badgeCabin}`}>
              <DoorOpen size={15} />
              <span>{data.cabin_code || "Main Desk"}</span>
            </div>
          </div>

          <div className={styles.destItem}>
            <div className={styles.destLabel}>Assigned Desk / Slot</div>
            <div className={`${styles.badgePill} ${styles.badgeDesk}`}>
              <Laptop size={15} />
              <span>{data.desk_code || "TBD"}</span>
            </div>
          </div>

          {(isPending || isApproved || isCheckedIn) && (
            <div className={styles.destGridFooter}>
              <span className={styles.destGridFooterText}>
                Need to meet someone else during this visit?
              </span>
              <button
                type="button"
                className={styles.updatePtmBtn}
                onClick={() => setShowPtmModal(true)}
                title="Apply to Meet Another Person"
              >
                <UserPlus size={14} />
                <span>Meet Another Person</span>
              </button>
            </div>
          )}
        </div>

        {/* Dynamic Instructions Box */}
        {isPending && (
          <div className={`${styles.instructionBox} ${styles.instructionPending}`}>
            <div className={styles.instructionIconWrapper}>
              <Hourglass size={18} />
            </div>
            <div>
              <div className={styles.instructionTitle}>Request Sent to Host</div>
              <div className={styles.instructionText}>
                Your entry request has been delivered to your host. If not answered within <strong>3 minutes</strong>,
                it will auto-escalate to the department head or ground receptionist. Please wait at the gate.
              </div>
            </div>
          </div>
        )}

        {isApproved && (
          <div className={`${styles.instructionBox} ${styles.instructionApproved}`}>
            <div className={styles.instructionIconWrapper}>
              <CheckCircle2 size={18} />
            </div>
            <div>
              <div className={styles.instructionTitle}>Entry Approved</div>
              <div className={styles.instructionText}>
                Present your pass code <strong>{data.pass_code}</strong> to the gate guard to check in.
                Proceed to Floor <strong>{data.floor_number || "1"}</strong>, Cabin <strong>{data.cabin_code || "Main Desk"}</strong>.
              </div>
            </div>
          </div>
        )}

        {isCheckedIn && (
          <div className={`${styles.instructionBox} ${styles.instructionCheckedIn}`}>
            <div className={styles.instructionIconWrapper}>
              <Building2 size={18} />
            </div>
            <div>
              <div className={styles.instructionTitle}>Checked In on Site</div>
              <div className={styles.instructionText}>
                You are verified on site. Please follow property guidelines. The security guard will log
                your checkout upon departure.
              </div>
            </div>
          </div>
        )}

        {isRejected && (
          <div className={`${styles.instructionBox} ${styles.instructionRejected}`}>
            <div className={styles.instructionIconWrapper}>
              <AlertCircle size={18} />
            </div>
            <div>
              <div className={styles.instructionTitle}>Request Not Approved</div>
              <div className={styles.instructionText}>
                Entry request was rejected: <em>{data.rejection_reason || "Rejected by host"}</em>.
                Please visit the Ground Floor Reception Desk for manual assistance.
              </div>
            </div>
          </div>
        )}

        {/* Check in Next Visitor Button: Only for Kiosk Terminal Mode or after Visit Checkout / Rejection */}
        {(isKioskMode || isCheckedOut || isRejected) && (
          <div style={{ marginTop: "24px" }}>
            <Button
              variant={isCheckedOut || isRejected ? "secondary" : "primary"}
              onClick={handleStartNewCheckin}
              icon={<PlusCircle size={16} />}
              style={{ width: "100%" }}
            >
              {isKioskMode ? "Check in Next Visitor (New Pass)" : "Start New Check-in"}
            </Button>
          </div>
        )}
      </div>

      {/* Update Person to Meet (PTM) Modal */}
      {showPtmModal && (
        <div className={styles.ptmModalOverlay} onClick={() => setShowPtmModal(false)}>
          <div className={styles.ptmModalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.ptmModalHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div className={styles.ptmModalIcon}>
                  <UserPlus size={18} />
                </div>
                <div>
                  <h3 className={styles.ptmModalTitle}>Apply to Meet Another Person</h3>
                  <p className={styles.ptmModalSubtitle}>Choose the new host employee or department</p>
                </div>
              </div>
              <button
                type="button"
                className={styles.ptmCloseBtn}
                onClick={() => setShowPtmModal(false)}
              >
                <X size={18} />
              </button>
            </div>

            {isCheckedIn && (
              <div style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                background: "#fef3c7",
                border: "1px solid #fde68a",
                color: "#92400e",
                padding: "10px 14px",
                borderRadius: "10px",
                fontSize: "0.8rem",
                lineHeight: "1.45",
                margin: "0 16px 14px 16px"
              }}>
                <AlertCircle size={16} style={{ flexShrink: 0, marginTop: "2px" }} />
                <div>
                  <strong>Already Checked-In:</strong> Submitting will immediately escalate notifications to your <strong>Floor Receptionist</strong>, <strong>Gate Security</strong>, <strong>New Host</strong>, and <strong>Previous Host</strong>.
                </div>
              </div>
            )}

            <div className={styles.ptmSearchWrap}>
              <Search size={16} className={styles.ptmSearchIcon} />
              <input
                type="text"
                placeholder="Search by name, role, department..."
                value={ptmSearchQuery}
                onChange={(e) => setPtmSearchQuery(e.target.value)}
                className={styles.ptmSearchInput}
                autoFocus
              />
            </div>

            <div className={styles.ptmHostList}>
              {searchingPtmHosts && (
                <div style={{ padding: "24px", textAlign: "center", color: "#64748b", fontSize: "0.85rem" }}>
                  Searching directory...
                </div>
              )}
              {!searchingPtmHosts && ptmHosts.length === 0 && (
                <div style={{ padding: "24px", textAlign: "center", color: "#64748b", fontSize: "0.85rem" }}>
                  No employees found matching &quot;{ptmSearchQuery}&quot;
                </div>
              )}
              {!searchingPtmHosts && ptmHosts.map((host) => {
                const isSelected = selectedPtmHost?.user_id === host.user_id;
                return (
                  <div
                    key={host.user_id}
                    className={`${styles.ptmHostItem} ${isSelected ? styles.ptmHostItemSelected : ""}`}
                    onClick={() => setSelectedPtmHost(host)}
                  >
                    <div className={styles.ptmHostAvatar}>
                      {host.full_name?.charAt(0)?.toUpperCase() || "E"}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className={styles.ptmHostName}>{host.full_name}</div>
                      <div className={styles.ptmHostMeta}>
                        {host.primary_role || host.designation || "Staff"} • {host.department_name || host.department || "General"}
                      </div>
                    </div>
                    {isSelected && (
                      <CheckCircle2 size={18} style={{ color: "#27306f" }} />
                    )}
                  </div>
                );
              })}
            </div>

            <div className={styles.ptmModalFooter}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowPtmModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={!selectedPtmHost || updatingPtm}
                onClick={handleUpdatePtm}
                icon={<UserCheck size={15} />}
              >
                {updatingPtm ? "Updating..." : "Confirm Person to Meet"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
